import { i18n } from '@lingui/core';
import { msg, t } from '@lingui/core/macro';
import { z } from 'zod';

import { filterAction } from '@/shared/filters/filter-action';
import { listPageAction } from '@/shared/patterns/list-search';
import { action, can, defineScreen } from '@/shared/screen/define';
import type { ScreenContext } from '@/shared/screen/types';

import { creditNoteApprovalFilters, internetBankingFilters } from './approvals.filters';
import { APPROVALS_PATH, APPROVALS_TAB_IDS, APPROVALS_TABS, CREDIT_NOTE_APPROVALS_PATH, DEFAULT_IB_TYPE, IB_TYPES, OPERATORS_PATH, type IbType } from './approvals.search';

// Manifestos da Central de Aprovações (U23/U42). Arquivo leve: entra no `src/app/screens.ts`. As ações
// de Operadores agem sobre as linhas marcadas na tela, nunca sobre um alvo escrito pelo agente; a tela
// liga o `run` real em `screens/approvals-agent.ts`.

/* eslint-disable lingui/no-unlocalized-strings -- respostas ao modelo, inglês canônico (U33) */
export const MODEL = {
  screen: 'This action only runs on the open approvals screen, on the rows selected there.',
  noSelection: 'No row is selected. The person selects the rows on screen; this action never picks rows by itself. Nothing was changed.',
  evaluated: 'At least one selected operator was already evaluated by the current user, so the screen hides Aprovar and Reprovar. Nothing was prepared.',
  prepared: 'Password and MFA code dialog opened on screen for the selected rows. NOTHING was approved or rejected: the person has to confirm there.',
  notPrepared: 'The decision could not be prepared (the screen shows the error). Nothing was sent.',
  confirmOpened: 'Confirmation dialog opened on screen. NOTHING was approved or rejected: the person has to confirm there.',
};
/* eslint-enable lingui/no-unlocalized-strings */

const needsScreen = () => {
  throw new Error(MODEL.screen);
};
const none = z.strictObject({});

const ROOT_ONLY = { rootTenancy: true } as const;

/**
 * Decisão com senha e código MFA (F2a) sobre as linhas marcadas (Operadores e Internet Banking).
 * `stepUp: true` ⇒ o `run` só prepara o diálogo; quem executa é a pessoa (U24).
 */
const stepUpDecision = (id: string, label: ReturnType<typeof msg>, kind: 'write' | 'destructive', description: ReturnType<typeof msg>, title: ReturnType<typeof msg>) =>
  action({
    id,
    kind,
    label,
    stepUp: true,
    description,
    input: none,
    preview: () => ({ title: i18n._(title), detail: i18n._(msg`Confirme com a sua senha e o código do aplicativo MFA.`), ...(kind === 'destructive' ? { tone: 'destructive' as const } : {}) }),
    run: needsScreen,
  });

// ─── Hub ───────────────────────────────────────────────────────────────────────────────────────────

export const approvalsOpenTab = action<{ tab: (typeof APPROVALS_TAB_IDS)[number] }>({
  id: 'approvals.openTab',
  kind: 'navigate',
  label: msg`Abrir aba`,
  description: msg`Abre uma aba da Central de Aprovações: internet-banking (Internet Banking) ou solicitacoes (Solicitações de conta).`,
  input: z.strictObject({ tab: z.enum(APPROVALS_TAB_IDS) }),
  run: ({ nav, input }) => (input.tab === 'internet-banking' ? nav.to(APPROVALS_PATH) : nav.to(APPROVALS_PATH, { search: { tab: input.tab } })),
});

/** "Atualizar" (BankAccuntRequestListContainer.tsx:84-89): o único jeito de carregar a lista, como no legado. */
export const requestsRefresh = action({
  id: 'approvals.requests.refresh',
  kind: 'read',
  label: msg`Atualizar`,
  description: msg`Busca (ou busca de novo) a fila de solicitações de conta pendentes da aba Solicitações de conta, que só é buscada assim. Como no UYZY atual, a tabela continua sem linhas. Nada é alterado.`,
  input: none,
  run: needsScreen,
});

export const requestsPage = listPageAction('approvals.requests.page');

// ─── Internet Banking ──────────────────────────────────────────────────────────────────────────────

/** O seletor de tipo (SelectBankAccountApprovalType.tsx): troca a lista e limpa a seleção (C:72). */
export const ibChooseType = action<{ type: IbType }>({
  id: 'approvals.internetBanking.type',
  kind: 'navigate',
  label: msg`Escolher o tipo`,
  description: msg`Mostra, na aba Internet Banking, as aprovações de um tipo: BankAccountUser (Operadores), BankAccountTransfer (Transferência), BankAccountPayment (Pagamentos), BankAccountBeneficiary (Favorecidos), BankAccountTransactionLimits (Limites) ou ComplianceReview (Revisão de Compliance). Os filtros continuam; as linhas marcadas são desmarcadas.`,
  input: z.strictObject({ type: z.enum(IB_TYPES) }),
  run: ({ nav, input }) => nav.search({ tab: undefined, tipo: input.type === DEFAULT_IB_TYPE ? undefined : input.type }),
});

/** "Atualizar" (BankAccountListContainer.tsx:278-283). */
export const ibRefresh = action({
  id: 'approvals.internetBanking.refresh',
  kind: 'read',
  label: msg`Atualizar`,
  description: msg`Busca de novo a lista de aprovações de Internet Banking do tipo escolhido. Nada é alterado.`,
  input: none,
  run: needsScreen,
});

export const ibPage = listPageAction('approvals.internetBanking.page');

export const ibFilter = filterAction('approvals.internetBanking.filter', internetBankingFilters, {
  description: msg`Filtra a lista de Internet Banking. Cada filtro é true (Sim) ou false (Não): filterOnlyMine (somente minhas solicitações), filterAlreadyApproved (que já respondi), filterFinished (já concluídas), filterOnlyImOperator (contas em que sou operador) e filterExpired (já expiradas). Filtro omitido é limpo.`,
});

/**
 * "Aprovar" e "Reprovar" da barra (BankAccountListContainer.tsx:266-277, :351-380): senha e código MFA (F2a) sobre as
 * linhas marcadas. `stepUp: true` ⇒ o `run` só prepara o diálogo, com a quantidade e o total; quem executa é a pessoa.
 */
export const ibApprove = stepUpDecision(
  'approvals.internetBanking.approve',
  msg`Aprovar`,
  'write',
  msg`Prepara a aprovação das linhas marcadas na lista de Internet Banking do tipo escolhido. Só abre o diálogo com a quantidade, o valor total e o pedido de senha e código MFA: esta ferramenta não aprova nada. Não recebe entrada; os alvos são as linhas marcadas na tela.`,
  msg`Aprovar`,
);

export const ibReject = stepUpDecision(
  'approvals.internetBanking.reject',
  msg`Reprovar`,
  'destructive',
  msg`Prepara a reprovação das linhas marcadas na lista de Internet Banking do tipo escolhido. Só abre o diálogo com a quantidade, o valor total e o pedido de senha e código MFA: esta ferramenta não reprova nada. Não recebe entrada; os alvos são as linhas marcadas na tela.`,
  msg`Reprovar`,
);

export const approvalsHubScreen = defineScreen({
  id: 'approvals.hub',
  path: APPROVALS_PATH,
  title: msg`Aprovações`,
  description: msg`Central de Aprovações da UY3. A aba Internet Banking lista o que espera decisão em seis tipos (operadores, transferências, pagamentos, favorecidos, limites e revisão de compliance), com filtros, detalhes e aprovar ou reprovar mediante senha e código MFA. A aba Solicitações de conta está desligada, como no UYZY atual: o botão Atualizar busca a fila, mas a tabela não mostra linhas.`,
  permission: can('PendingApprovals', 'Read'),
  access: ROOT_ONLY,
  place: { icon: 'badge-check', section: 'approvals', tabs: APPROVALS_TABS },
  actions: [approvalsOpenTab, requestsRefresh, requestsPage, ibChooseType, ibRefresh, ibPage, ibFilter, ibApprove, ibReject],
});

/** O que o agente lê do hub (U46 D2-C): a aba, se a fila já foi buscada e a contagem; nada do titular. */
export function approvalsHubContext(input: { readonly tab: string; readonly tabLabel: string; readonly loaded: boolean; readonly total: number | undefined }): ScreenContext {
  const tabLabel = input.tabLabel;
  const total = input.total;
  const parts = [t`Central de Aprovações, aba ${tabLabel}.`];
  if (input.tab === 'solicitacoes') {
    parts.push(t`A aba está desligada, como no UYZY atual: a tabela não mostra linhas e não há ações sobre elas.`);
    if (!input.loaded) parts.push(t`A fila ainda não foi buscada: só pelo Atualizar.`);
    else parts.push(total === undefined ? t`Carregando.` : t`A fila tem ${total} solicitações pendentes.`);
  }
  return { summary: parts.join(' '), fields: { tab: input.tab, loaded: input.loaded, total: input.total ?? null }, sensitivity: 'internal' };
}

/**
 * O que o agente lê da aba Internet Banking (U46 D2-C): o tipo, a contagem, os filtros e a seleção com o total.
 * Nada de nome, documento, conta, chave Pix nem anexo.
 */
export function internetBankingContext(input: {
  readonly type: IbType;
  readonly typeLabel: string;
  readonly total: number | undefined;
  readonly selected: number;
  readonly selectedTotal: string | null;
  readonly filters: readonly string[];
}): ScreenContext {
  const { typeLabel, total, selected, selectedTotal } = input;
  const filters = input.filters.join(', ');
  const parts = [t`Central de Aprovações, aba Internet Banking, tipo ${typeLabel}.`];
  parts.push(total === undefined ? t`Carregando.` : t`${total} aprovações pendentes.`);
  if (input.filters.length > 0) parts.push(t`Filtros aplicados: ${filters}.`);
  parts.push(selectedTotal === null ? t`${selected} marcadas.` : t`${selected} marcadas, valor total ${selectedTotal}.`);
  return {
    summary: parts.join(' '),
    fields: { tab: 'internet-banking', type: input.type, total: total ?? null, selected, selectedTotal, filters: [...input.filters] },
    sensitivity: 'internal',
  };
}

// ─── Operadores ──────────────────────────────────────────────────────────────────────────────────

/** "Atualizar" (BankAccountOperatorContainer.tsx:210-220). */
export const operatorsRefresh = action({
  id: 'approvals.operators.refresh',
  kind: 'read',
  label: msg`Atualizar`,
  description: msg`Busca de novo a lista de operadores pendentes de aprovação. Nada é alterado.`,
  input: none,
  run: needsScreen,
});

export const operatorsPage = listPageAction('approvals.operators.page');

export const operatorsApprove = stepUpDecision(
  'approvals.operators.approve',
  msg`Aprovar`,
  'write',
  msg`Prepara a aprovação dos operadores marcados na lista de operadores pendentes. Só abre o diálogo de senha e código MFA: esta ferramenta não aprova nada. Não recebe entrada; os alvos são as linhas marcadas na tela.`,
  msg`Aprovar operador`,
);

export const operatorsReject = stepUpDecision(
  'approvals.operators.reject',
  msg`Reprovar`,
  'destructive',
  msg`Prepara a reprovação dos operadores marcados na lista de operadores pendentes. Só abre o diálogo de senha e código MFA: esta ferramenta não reprova nada. Não recebe entrada; os alvos são as linhas marcadas na tela.`,
  msg`Rejeitar operador`,
);

export const approvalsOperatorsScreen = defineScreen({
  id: 'approvals.operators',
  path: OPERATORS_PATH,
  title: msg`Operadores`,
  description: msg`Operadores de contas digitais aguardando a aprovação administrativa da UY3: titular, conta, operador, status e nível, com aprovar e reprovar em lote mediante senha e código MFA.`,
  permission: can('BankAccount', 'ViewPendingOperators'),
  access: ROOT_ONLY,
  place: { icon: 'user', parent: 'approvals.hub' },
  actions: [operatorsRefresh, operatorsPage, operatorsApprove, operatorsReject],
});

/** O que o agente lê dos operadores (U46 D2-C): contagem e marcadas; sem nome nem documento. */
export function approvalsOperatorsContext(input: { readonly total: number | undefined; readonly selected: number; readonly evaluated: boolean }): ScreenContext {
  const total = input.total;
  const selected = input.selected;
  return {
    summary: [
      t`Operadores pendentes de aprovação.`,
      total === undefined ? t`Carregando.` : t`${total} operadores.`,
      t`${selected} marcados.`,
      input.evaluated ? t`Algum marcado já foi avaliado por você: Aprovar e Reprovar ficam escondidos.` : '',
    ]
      .filter(Boolean)
      .join(' '),
    fields: { total: input.total ?? null, selected: input.selected, evaluated: input.evaluated },
    sensitivity: 'internal',
  };
}

// ─── Operações (R6) ──────────────────────────────────────────────────────────────────────────────

/** "Atualizar" (CreditNoteListApprovalsContainer.tsx:112-117): o único carregamento, como no legado (`enabled: false`). */
export const creditNotesRefresh = action({
  id: 'approvals.creditNotes.refresh',
  kind: 'read',
  label: msg`Atualizar`,
  description: msg`Busca (ou busca de novo) a lista de operações de crédito que esperam aprovação, com a página e os filtros que estão na tela. Como no UYZY atual, a lista só é buscada assim. Nada é alterado.`,
  input: none,
  run: needsScreen,
});

export const creditNotesPage = listPageAction('approvals.creditNotes.page');

export const creditNotesFilter = filterAction('approvals.creditNotes.filter', creditNoteApprovalFilters, {
  description: msg`Filtra a lista de operações pendentes: status (vários), personId (tomador), initialDate (data de início; só o início vale), maxValue e minValue (em reais), tenant (correspondente) e creditNoteNo (número da operação). Filtro omitido é limpo. Depois de filtrar, a lista só aparece pelo Atualizar.`,
});

/** Aprovar e Reprovar sobre as marcadas: a ferramenta só abre o diálogo da tela; quem confirma é a pessoa. */
const confirmDecision = (id: string, label: ReturnType<typeof msg>, kind: 'write' | 'destructive', description: ReturnType<typeof msg>, title: ReturnType<typeof msg>, detail: ReturnType<typeof msg>) =>
  action({
    id,
    kind,
    label,
    description,
    input: none,
    preview: () => ({ title: i18n._(title), detail: i18n._(detail), ...(kind === 'destructive' ? { tone: 'destructive' as const } : {}) }),
    run: needsScreen,
  });

/** "Aprovar" da barra (CreditNoteListApprovalsContainer.tsx:100-105): um POST por operação, com a ação da CCB do tipo. */
export const creditNotesApprove = confirmDecision(
  'approvals.creditNotes.approve',
  msg`Aprovar`,
  'write',
  msg`Prepara a aprovação das operações marcadas na página que está na tela. Só abre o diálogo "Aprovar operação": esta ferramenta não aprova nada. Não recebe entrada; os alvos são as linhas marcadas.`,
  msg`Aprovar operação`,
  msg`Tem certeza que deseja aprovar essa operação?`,
);

/** "Reprovar" da barra (CreditNoteListApprovalsContainer.tsx:106-111): motivo, bloqueio e data, um POST por operação. */
export const creditNotesReject = confirmDecision(
  'approvals.creditNotes.reject',
  msg`Reprovar`,
  'destructive',
  msg`Prepara a reprovação das operações marcadas na página que está na tela. Só abre o diálogo "Reprovar operação", com motivo, bloqueio e data de bloqueio: esta ferramenta não reprova nada. Não recebe entrada; os alvos são as linhas marcadas.`,
  msg`Reprovar operação`,
  msg`Tem certeza que deseja reprovar essa operação?`,
);

export const approvalsCreditNoteScreen = defineScreen({
  id: 'approvals.creditNote',
  path: CREDIT_NOTE_APPROVALS_PATH,
  title: msg`Operações`,
  description: msg`Operações de crédito que esperam uma etapa de aprovação da UY3 (compliance, crédito, instrumento, assinatura, liquidação ou revisão de pagamento): correspondente, número, tomador, status, valor e tipo de solicitação, com filtros, detalhes e aprovar ou reprovar uma ou várias. Como no UYZY atual, a lista só é buscada pelo Atualizar.`,
  permission: can('PendingApprovals', 'Read'),
  access: ROOT_ONLY,
  place: { icon: 'file-text', parent: 'approvals.hub' },
  actions: [creditNotesRefresh, creditNotesPage, creditNotesFilter, creditNotesApprove, creditNotesReject],
});

/** O que o agente lê de Operações (U46 D2-C): se a lista foi buscada, a contagem, os filtros e as marcadas; nada do tomador. */
export function approvalsCreditNoteContext(input: { readonly loaded: boolean; readonly total: number | undefined; readonly selected: number; readonly visibleSelected: number; readonly filters: readonly string[] }): ScreenContext {
  const { total, selected, visibleSelected } = input;
  const filters = input.filters.join(', ');
  const parts = [t`Operações pendentes de aprovação.`];
  if (!input.loaded) parts.push(t`A lista ainda não foi buscada para esta página e estes filtros: só pelo Atualizar.`);
  else parts.push(total === undefined ? t`Carregando.` : t`${total} operações pendentes.`);
  if (input.filters.length > 0) parts.push(t`Filtros aplicados: ${filters}.`);
  parts.push(t`${selected} marcadas, ${visibleSelected} nesta página.`);
  return {
    summary: parts.join(' '),
    fields: { loaded: input.loaded, total: total ?? null, selected, visibleSelected, filters: [...input.filters] },
    sensitivity: 'internal',
  };
}
